import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createTask,
  deleteTask,
  listTasks,
  updateTask,
  listProjects,
  getLastDisciplinePreference,
  setLastDisciplinePreference,
  listCompanyMembers,
  listProjectMembers,
  listTaskShares,
  createTaskShare,
  revokeTaskShare,
  dismissTaskShare,
} from "@/lib/backend";
import { demoStorageKey, isDemoModeScoped } from "@/lib/demoMode";
import type {
  Task,
  TaskStatus,
  TaskPriority,
  TaskDiscipline,
  TaskType,
  ProjectSummary,
  CreateTaskInput,
  UpdateTaskInput,
  SharePermission,
  CompanyMember,
  ProjectMember,
} from "@/lib/types";
import type { ListSharesResponse } from "@shared/api";
import { toast } from "@/hooks/use-toast";
import { useWorkspace } from "@/components/layout/workspace-context";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { isPinned, loadPinnedIds, setPinned } from "@/lib/pins";
import { getMiscProject, isMiscProjectId } from "@/lib/miscProject";
import {
  buildClientOptions,
  buildLocationOptions,
  buildProjectOptions,
  filterProjectsForSelection,
  getLocationLabel,
  normalizeClientKey,
  normalizeLocationKey,
  normalizeProjectSelection,
  selectionFromProjectId,
} from "@/lib/projectSelection";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Filter, Pencil, Pin, PinOff, Trash2, Calendar as CalendarIcon, Share2, X, Paperclip, Maximize2, Minimize2 } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import TaskGrid from "./TaskGrid";
import TaskDetailDrawer from "./TaskDetailDrawer";
import { ModalPanel } from "@/components/ui/modal-panel";
import { CloseButton } from "@/components/ui/close-button";

const TYPE_OPTIONS: TaskType[] = ["Design", "Coordination", "Submittal", "RFI/ASI", "PCR/PCO", "Deliverable"];
const STATUS_OPTIONS: TaskStatus[] = ["pending", "in_progress", "coordinating", "complete"];
const DISCIPLINE_OPTIONS: TaskDiscipline[] = ["Mechanical", "Electrical", "Lighting", "Plumbing", "Technology", "Fire Protection", "Misc"];
const panelClass =
"rounded-3xl bg-black/70 ring-1 ring-[#7b2ee6]/60 shadow-[0_0_45px_rgba(123,46,230,0.35)] backdrop-blur-md";

const formControlClass =
"w-full text-sm border rounded px-2 py-1 bg-[#1a1a1a] text-white";

function parseLocalDateOnly(dateStr?: string | null): Date | null {
  if (!dateStr) return null;
  const src = String(dateStr);
  if (/^\d{4}-\d{2}-\d{2}/.test(src)) {
    const [y, m, d] = src.slice(0, 10).split("-").map(Number);
    return new Date(y, (m || 1) - 1, d || 1);
  }
  const d = new Date(src);
  if (isNaN(d.getTime())) return null;
  return d;
}

function sortTasks(a: Task, b: Task): number {
  const aDate = parseLocalDateOnly(a.due_date);
  const bDate = parseLocalDateOnly(b.due_date);
  const aDue = aDate ? aDate.getTime() : 0;
  const bDue = bDate ? bDate.getTime() : 0;
  if (aDue && bDue && aDue !== bDue) return aDue - bDue;
  return a.created_at.localeCompare(b.created_at);
}

function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "";
  const src = String(dateStr);
  try {
    if (/^\d{4}-\d{2}-\d{2}/.test(src)) {
      const [y, m, d] = src.slice(0, 10).split("-").map(Number);
      const date = new Date(y, (m || 1) - 1, d || 1);
      return date.toLocaleDateString();
    }
    const date = new Date(src);
    if (isNaN(date.getTime())) return src;
    return date.toLocaleDateString();
  } catch {
    return src;
  }
}

function classifyDueDate(dateStr?: string | null): "today" | "past" | "future" | null {
  if (!dateStr) return null;
  try {
    const d = parseLocalDateOnly(dateStr);
    if (!d) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    if (dd.getTime() === today.getTime()) return "today";
    return dd.getTime() < today.getTime() ? "past" : "future";
  } catch {
    return null;
  }
}

function formatDateForInput(dateStr?: string | null): string {
  if (!dateStr) return "";
  try {
    const src = String(dateStr);
    if (/^\d{4}-\d{2}-\d{2}/.test(src)) return src.slice(0, 10);
    const date = new Date(src);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().slice(0, 10);
  } catch {
    return "";
  }
}

function getStatusBadge(status: TaskStatus): string {
  switch (status) {
    case "pending":
      return "bg-yellow-500/20 text-yellow-300";
    case "in_progress":
      return "bg-blue-500/20 text-blue-300";
    case "coordinating":
      return "bg-purple-500/20 text-purple-200";
    case "complete":
      return "bg-green-600/20 text-green-300";
    default:
      return "";
  }
}

function getStatusLabel(status: TaskStatus): string {
  switch (status) {
    case "pending":
      return "Pending";
    case "in_progress":
      return "In Progress";
    case "coordinating":
      return "Coordinating";
    case "complete":
      return "Complete";
    default:
      return status;
  }
}

function getTypeBadge(typeText?: string | null): string {
  const t = (typeText || "").toLowerCase();
  if (t === "design") return "bg-blue-500/20 text-blue-300";
  if (t === "coordination") return "bg-emerald-600/20 text-emerald-300";
  if (t === "submittal") return "bg-orange-500/20 text-orange-300";
  if (t === "rfi/asi") return "bg-indigo-500/20 text-indigo-300";
  if (t === "pcr/pco") return "bg-pink-500/20 text-pink-300";
  if (t === "deliverable") return "bg-teal-500/20 text-teal-200";
  return "bg-gray-500/20 text-gray-300";
}

function normalizeStatus(value: any): TaskStatus {
  // Map legacy values to new ones for backward compatibility
  if (value === "todo") return "pending";
  if (value === "doing") return "in_progress";
  if (value === "done") return "complete";
  if (value === "in-progress") return "in_progress";
  if (value === "coordinating") return "coordinating";
  if (value === "pending" || value === "in_progress" || value === "complete") return value;
  return "pending";
}

function parseFilterDate(input: string): Date | null {
  if (!input) return null;
  const src = input.trim();
  if (!src) return null;
  const m = src.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2}|\d{4})$/);
  if (m) {
    const month = Number(m[1]);
    const day = Number(m[2]);
    let year = Number(m[3]);
    if (year < 100) {
      year += year >= 70 ? 1900 : 2000;
    }
    if (!year || !month || !day) return null;
    const d = new Date(year, month - 1, day);
    if (isNaN(d.getTime())) return null;
    return d;
  }
  const d = parseLocalDateOnly(src);
  return d && !isNaN(d.getTime()) ? d : null;
}

function formatFilterDateForDisplay(date: Date | null): string {
  if (!date) return "";
  const m = (date.getMonth() + 1).toString().padStart(2, "0");
  const d = date.getDate().toString().padStart(2, "0");
  const yy = (date.getFullYear() % 100).toString().padStart(2, "0");
  return `${m}/${d}/${yy}`;
}

function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

type UserHeaders = { id?: string; email?: string };

interface TasksPanelProps {
  tabId?: string;
  user?: UserHeaders;
  onDirtyChange?: (dirty: boolean) => void;
  projectId?: string | null;
  projectName?: string | null;
  isMiscProject?: boolean;
}

export default function TasksPanel({ tabId, user, onDirtyChange, projectId, projectName, isMiscProject }: TasksPanelProps) {
  const { setTabScope } = useWorkspace();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<Task | null>(null);
  const [confirmDeleteTask, setConfirmDeleteTask] = useState<Task | null>(null);
  const [useLegacyStatus, setUseLegacyStatus] = useState<boolean>(false);
  const containerRef = useState(() => ({ current: null as unknown as HTMLElement | null }))[0] as React.MutableRefObject<HTMLElement | null>;
  const [isCompletedOpen, setIsCompletedOpen] = useState(false);
  type SortKey = 'type' | 'title' | 'discipline' | 'priority' | 'due_date';
  type SortDir = 'asc' | 'desc';
  const [sort, setSort] = useState<{ key: SortKey | null; dir: SortDir }>({ key: null, dir: 'asc' });
  const [searchCompleted, setSearchCompleted] = useState("");
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [projectsLoading, setProjectsLoading] = useState<boolean>(false);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [pinnedTaskIds, setPinnedTaskIds] = useState<Set<string>>(() => new Set());
  const [projectClientFilter, setProjectClientFilter] = useState<string>("all");
  const [projectLocationFilter, setProjectLocationFilter] = useState<string>("all");
  const [projectFilter, setProjectFilter] = useState<string>("all");
  const [shareOpen, setShareOpen] = useState(false);
  const [shareTask, setShareTask] = useState<Task | null>(null);
  const [shareMembers, setShareMembers] = useState<Array<{ user_id: string; label: string }>>([]);
  const [shareRecipient, setShareRecipient] = useState<string>("");
  const [sharePermission, setSharePermission] = useState<SharePermission>("read");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  const [shareIncludeRevoked, setShareIncludeRevoked] = useState(false);
  const [shareList, setShareList] = useState<ListSharesResponse | null>(null);
  const [userDisplayNames, setUserDisplayNames] = useState<Record<string, string>>({});

  const [selectedTask, setSelectedTask] = useState<Task | null>(null);  
  const demoKey = useMemo(() => demoStorageKey("tasks"), []);
  const isDemo = useMemo(
    () => isDemoModeScoped({ userId: user?.id, email: user?.email }) && Boolean(demoKey),
    [demoKey, user?.id, user?.email],
  );

  const persistDemoSnapshot = useCallback(
    (next: Task[]) => {
      if (!isDemo || !demoKey) return;
      try {
        window.localStorage.setItem(demoKey, JSON.stringify(next || []));
      } catch {
        // ignore storage failures
      }
    },
    [demoKey, isDemo],
  );

  const readDemoSnapshot = useCallback((): Task[] | null => {
    if (!isDemo || !demoKey) return null;
    try {
      const raw = window.localStorage.getItem(demoKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? (parsed as Task[]) : null;
    } catch {
      return null;
    }
  }, [demoKey, isDemo]);
  const loadedProjectMembersRef = useRef<Set<string>>(new Set());
  const loadedCompanyMembersRef = useRef<Set<string>>(new Set());
  const loggedUnknownCreatorRef = useRef<Set<string>>(new Set());

  // Filter controls
  const [statusFilter, setStatusFilter] = useState<TaskStatus | "all">("all");
  const [typeFilter, setTypeFilter] = useState<TaskType | "all">("all");
  const [disciplineFilter, setDisciplineFilter] = useState<TaskDiscipline | "all">("all");
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | "all">("all");
  type DateFilterField = "due" | "completed" | null;
  const [dateFilterField, setDateFilterField] = useState<DateFilterField>(null);
  const [dateFilterStart, setDateFilterStart] = useState<string>("");
  const [dateFilterEnd, setDateFilterEnd] = useState<string>("");

  // Inline create form fields
  const [newType, setNewType] = useState<TaskType>(TYPE_OPTIONS[0]);
  const [newTitle, setNewTitle] = useState<string>("");
  const [newDueBy, setNewDueBy] = useState<string>("");
  const [newDescription, setNewDescription] = useState<string>("");
  const [newDescriptionExpanded, setNewDescriptionExpanded] = useState<boolean>(false);
  const [newPriority, setNewPriority] = useState<TaskPriority | "">("");
  const [newDiscipline, setNewDisciplineRaw] = useState<TaskDiscipline | "">(getLastDisciplinePreference() ?? "");
  // Tracks whether the user has explicitly changed the discipline in this session.
  // The initial value comes from a persisted preference, not user input, so it must
  // not count as a staged change on its own.
  const [newDisciplineModified, setNewDisciplineModified] = useState(false);
  const setNewDiscipline = useCallback((value: TaskDiscipline | "") => {
    setNewDisciplineRaw(value);
    setNewDisciplineModified(true);
  }, []);
  const [newClientKey, setNewClientKey] = useState<string>("");
  const [newLocationKey, setNewLocationKey] = useState<string>("");
  const [newProjectId, setNewProjectId] = useState<string>("");

  // Edit modal fields
  const [formTitle, setFormTitle] = useState("");
  const [formDescription, setFormDescription] = useState("");
  const [formStatus, setFormStatus] = useState<TaskStatus>("pending");
  const [formPriority, setFormPriority] = useState<TaskPriority | "">("");
  const [formDiscipline, setFormDiscipline] = useState<TaskDiscipline | "">("");
  const [formDueDate, setFormDueDate] = useState("");
  const [formLabels, setFormLabels] = useState("");
  const [formType, setFormType] = useState<TaskType | "">("");
  const [formClientKey, setFormClientKey] = useState<string>("");
  const [formLocationKey, setFormLocationKey] = useState<string>("");
  const [formProjectId, setFormProjectId] = useState<string>("");
  const [formCompletedAt, setFormCompletedAt] = useState<string>("");
  const [formIsMiscScoped, setFormIsMiscScoped] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const miscProject = useMemo(() => getMiscProject(projects), [projects]);
  const miscProjectId = miscProject?.id ?? null;

  // Default new tasks to the current scope - responds to all scope changes
  useEffect(() => {
    if (isMiscProject && isMiscProjectId(projectId, miscProjectId)) {
      setNewProjectId("");
    } else if (projectId) {
      const selection = selectionFromProjectId(projects, projectId);
      if (selection) {
        setNewClientKey(selection.clientKey);
        setNewLocationKey(selection.locationKey);
        setNewProjectId(selection.projectId);
      }
    } else {
      // Unscoped - reset to empty
      setNewProjectId("");
    }
  }, [projectId, isMiscProject, projects, miscProjectId]);

  const projectById = useMemo(() => {
    const map = new Map<string, ProjectSummary>();
    projects.forEach((project) => {
      if (project.id) {
        map.set(project.id, project);
      }
    });
    return map;
  }, [projects]);

  const canUseProjectForTasks = useCallback((proj: ProjectSummary): boolean => {
    // Backend can return "Project not found" when the caller isn't a project member/admin.
    // The `/projects` list may include projects in a company that the user can't act on.
    if (!proj?.id) return false;
    const name = (proj.project_name || "").trim().toLowerCase();
    if (name === "misc") return false; // keep Misc on the explicit checkbox path instead of general project pickers
    return proj.capabilities?.tasks_create === true;
  }, []);
  const canUseProjectForForm = useCallback(
    (proj: ProjectSummary): boolean => canUseProjectForTasks(proj) || proj.id === formProjectId,
    [canUseProjectForTasks, formProjectId],
  );

  const projectOptions = useMemo(() => {
    const sorted = projects
      // Only show projects the user can actually act on; avoid backend 404 "Project not found"
      .filter(canUseProjectForTasks)
      .slice()
      .sort((a, b) => (a.project_name || "").localeCompare(b.project_name || ""));
    return sorted.map((proj) => {
      const parts = [proj.project_name];
      if (proj.client_name) parts.push(proj.client_name);
      return { value: proj.id, label: parts.filter(Boolean).join(" • "), company_id: proj.company_id ?? null };
    });
  }, [projects]);

  const allowedProjectIds = useMemo(() => new Set(projectOptions.map((opt) => opt.value)), [projectOptions]);

  // If a previously-selected project becomes invalid for this user/session, clear it proactively.
  useEffect(() => {
    if (!newProjectId) return;
    if (allowedProjectIds.has(newProjectId)) return;
    setNewProjectId("");
  }, [newProjectId, allowedProjectIds]);

  const newClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForTasks }),
    [projects, canUseProjectForTasks],
  );
  const newLocationOptions = useMemo(
    () => buildLocationOptions(projects, newClientKey, { filter: canUseProjectForTasks }),
    [projects, newClientKey, canUseProjectForTasks],
  );
  const newProjectsForLocation = useMemo(
    () =>
      filterProjectsForSelection(
        projects,
        { clientKey: newClientKey, locationKey: newLocationKey },
        { filter: canUseProjectForTasks },
      ),
    [projects, newClientKey, newLocationKey, canUseProjectForTasks],
  );
  const newProjectOptions = useMemo(
    () => buildProjectOptions(newProjectsForLocation),
    [newProjectsForLocation],
  );

  const formClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForForm }),
    [projects, canUseProjectForForm],
  );
  const formLocationOptions = useMemo(
    () => buildLocationOptions(projects, formClientKey, { filter: canUseProjectForForm }),
    [projects, formClientKey, canUseProjectForForm],
  );
  const formProjectsForLocation = useMemo(
    () =>
      filterProjectsForSelection(
        projects,
        { clientKey: formClientKey, locationKey: formLocationKey },
        { filter: canUseProjectForForm },
      ),
    [projects, formClientKey, formLocationKey, canUseProjectForForm],
  );
  const formProjectOptions = useMemo(
    () => buildProjectOptions(formProjectsForLocation),
    [formProjectsForLocation],
  );

  const filterClientOptions = useMemo(
    () => buildClientOptions(projects, { filter: canUseProjectForTasks }),
    [projects, canUseProjectForTasks],
  );
  const filterLocationOptions = useMemo(() => {
    const base = projects.filter(canUseProjectForTasks);
    if (projectClientFilter === "all") {
      const map = new Map<string, { key: string; label: string }>();
      base.forEach((project) => {
        const key = normalizeLocationKey(project);
        if (!map.has(key)) {
          map.set(key, { key, label: getLocationLabel(project) });
        }
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }
    return buildLocationOptions(base, projectClientFilter);
  }, [projects, projectClientFilter, canUseProjectForTasks]);
  const filterProjectsForOptions = useMemo(() => {
    const base = projects.filter(canUseProjectForTasks);
    if (projectClientFilter === "all" && projectLocationFilter === "all") {
      return base;
    }
    if (projectClientFilter === "all" && projectLocationFilter !== "all") {
      return base.filter((project) => normalizeLocationKey(project) === projectLocationFilter);
    }
    if (projectClientFilter !== "all" && projectLocationFilter === "all") {
      return base.filter((project) => normalizeClientKey(project) === projectClientFilter);
    }
    return filterProjectsForSelection(
      base,
      { clientKey: projectClientFilter, locationKey: projectLocationFilter },
    );
  }, [projects, projectClientFilter, projectLocationFilter, canUseProjectForTasks]);
  const filterProjectOptions = useMemo(
    () => buildProjectOptions(filterProjectsForOptions),
    [filterProjectsForOptions],
  );

  useEffect(() => {
    const normalized = normalizeProjectSelection(
      projects,
      { clientKey: newClientKey, locationKey: newLocationKey, projectId: newProjectId },
    { filter: canUseProjectForTasks, preserveEmptyProject: true },
    );
    if (normalized.clientKey !== newClientKey) setNewClientKey(normalized.clientKey);
    if (normalized.locationKey !== newLocationKey) setNewLocationKey(normalized.locationKey);
    if (normalized.projectId !== newProjectId) setNewProjectId(normalized.projectId);
  }, [projects, newClientKey, newLocationKey, newProjectId, canUseProjectForTasks]);

  useEffect(() => {
    const normalized = normalizeProjectSelection(
      projects,
      { clientKey: formClientKey, locationKey: formLocationKey, projectId: formProjectId },
      { filter: canUseProjectForForm, preserveEmptyProject: true },
    );
    if (normalized.clientKey !== formClientKey) setFormClientKey(normalized.clientKey);
    if (normalized.locationKey !== formLocationKey) setFormLocationKey(normalized.locationKey);
    if (normalized.projectId !== formProjectId) setFormProjectId(normalized.projectId);
  }, [projects, formClientKey, formLocationKey, formProjectId, canUseProjectForForm]);

  useEffect(() => {
    if (projectFilter === "all" || projectFilter === "__misc__") return;
    const selection = selectionFromProjectId(projects, projectFilter);
    if (!selection) return;
    if (projectClientFilter !== selection.clientKey) setProjectClientFilter(selection.clientKey);
    if (projectLocationFilter !== selection.locationKey) setProjectLocationFilter(selection.locationKey);
  }, [projectFilter, projects, projectClientFilter, projectLocationFilter]);

  useEffect(() => {
    if (projectLocationFilter === "all") return;
    const locationKeys = filterLocationOptions.map((loc) => loc.key);
    if (!locationKeys.includes(projectLocationFilter)) {
      setProjectLocationFilter("all");
    }
  }, [filterLocationOptions, projectLocationFilter]);

  useEffect(() => {
    if (projectFilter === "all" || projectFilter === "__misc__") return;
    if (projects.length === 0) return;
    const allowed = new Set(filterProjectOptions.map((opt) => opt.value));
    if (!allowed.has(projectFilter)) {
      setProjectFilter("all");
    }
  }, [projectFilter, filterProjectOptions, projects.length]);

  // Handle all scope transitions: initial mount, project changes, misc changes, and unscoped changes
  const prevScopeRef = useRef<{ projectId: string | null; isMisc: boolean } | null>(null);
  // Synchronous flag: when true, the setTabScope effect must skip because
  // we just pushed a filter update from incoming props (not from user action).
  const skipSyncBackRef = useRef(false);
  
  useEffect(() => {
    const prev = prevScopeRef.current;
    const current = { projectId: projectId ?? null, isMisc: Boolean(isMiscProject) };
    
    // Detect ANY scope change (not just to unscoped)
    const scopeChanged = prev && (
      prev.projectId !== current.projectId || 
      prev.isMisc !== current.isMisc
    );
    
    if (scopeChanged) {
      // Tell the setTabScope effect to skip — this filter change comes from props, not user action
      skipSyncBackRef.current = true;

      // Update filter to match new scope
      if (current.isMisc) {
        // Transitioning to Misc bucket
        setProjectFilter("__misc__");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      } else if (current.projectId) {
        // Transitioning to a specific project
        const selection = selectionFromProjectId(projects, current.projectId);
        if (selection) {
          setProjectClientFilter(selection.clientKey);
          setProjectLocationFilter(selection.locationKey);
        }
        setProjectFilter(current.projectId);
      } else {
        // Transitioning to unscoped "all" view
        setProjectFilter("all");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      }
      
      // Clear other filters on any scope change
      setStatusFilter("all");
      setTypeFilter("all");
      setDisciplineFilter("all");
      setPriorityFilter("all");
      setDateFilterField(null);
      setDateFilterStart("");
      setDateFilterEnd("");
    } else if (!prev) {
      // Initial mount - set filter based on initial scope
      // Also skip sync-back for initial mount
      skipSyncBackRef.current = true;

      if (current.isMisc) {
        setProjectFilter("__misc__");
        setProjectClientFilter("all");
        setProjectLocationFilter("all");
      } else if (current.projectId) {
        const selection = selectionFromProjectId(projects, current.projectId);
        if (selection) {
          setProjectClientFilter(selection.clientKey);
          setProjectLocationFilter(selection.locationKey);
        }
        setProjectFilter(current.projectId);
      }
      // else: leave at default "all"
    }
    
    prevScopeRef.current = current;
  }, [projectId, isMiscProject, projects]);

  // Sync filter state back to Workspace when user manually changes filters.
  // Skipped when the filter change was triggered by incoming props (skipSyncBackRef).
  useEffect(() => {
    if (!tabId) return;

    // If prevScopeRef just pushed a filter update from props, skip this run
    if (skipSyncBackRef.current) {
      skipSyncBackRef.current = false;
      return;
    }
    
    // Determine what the filter SHOULD be based on current props
    const expectedFilter = isMiscProject
      ? "__misc__"
      : projectId
        ? projectId
        : "all";
    
    // Only call setTabScope if user changed the filter to something different than props
    if (projectFilter === expectedFilter) {
      return; // Filter matches props, no need to sync back
    }
    
    // User manually changed the filter, sync it back to Workspace
    if (projectFilter === "__misc__") {
      setTabScope(tabId, {
        projectId: miscProjectId,
        projectName: miscProject?.project_name ?? "Misc",
        clientName: miscProject?.client_name ?? null,
        isMiscProject: true,
      });
      return;
    }
    if (projectFilter === "all") {
      setTabScope(tabId, { projectId: null, projectName: null, clientName: null, isMiscProject: false });
      return;
    }
    const meta = projectById.get(projectFilter);
    setTabScope(tabId, {
      projectId: projectFilter,
      projectName: meta?.project_name ?? projectName ?? null,
      clientName: meta?.client_name ?? null,
      isMiscProject: false,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabId, projectFilter, projectById, setTabScope, miscProjectId, miscProject]);

  const editHasChanges = useMemo(() => {
    if (!editing) return false;
    const trimmedTitle = formTitle.trim();
    const titleChanged = Boolean(trimmedTitle) && trimmedTitle !== (editing.title || "");
    const nextDescription = formDescription.trim() || null;
    const descriptionChanged = nextDescription !== (editing.description ?? null);
    const nextPriority = (formPriority || null) as TaskPriority | null;
    const priorityChanged = nextPriority !== (editing.priority ?? null);
    const nextDiscipline = (formDiscipline || null) as TaskDiscipline | null;
    const disciplineChanged = nextDiscipline !== (editing.discipline ?? null);
    const isoFormDue = formDueDate ? formDueDate : null;
    const prevDueNormalized = formatDateForInput(editing.due_date);
    const dueChanged = (isoFormDue || "") !== (prevDueNormalized || "");
    const prevTypeRaw = (editing.task_type ?? (editing.labels?.[0] ?? "") ?? "").toString().trim();
    const normalizedType = (formType || "").toString().trim();
    const typeChanged = prevTypeRaw.toLowerCase() !== normalizedType.toLowerCase();
    const labels = formLabels.trim() ? formLabels.split(",").map((l) => l.trim()).filter(Boolean) : null;
    const extra = (labels || []).filter((l) => l.toLowerCase() !== normalizedType.toLowerCase());
    const combined = normalizedType ? [normalizedType, ...extra].filter(Boolean) : extra;
    const nextLabels = combined.length ? combined : null;
    const prevLabelsJson = JSON.stringify(editing.labels ?? null);
    const nextLabelsJson = JSON.stringify(nextLabels);
    const labelsChanged = nextLabelsJson !== prevLabelsJson;
    const prevUiStatus = normalizeStatus(editing.status);
    const statusChanged = prevUiStatus !== formStatus;
    const prevProjectId = editing.project_id ?? "";
    const effectiveProjectId = formIsMiscScoped ? (miscProjectId ?? "") : (formProjectId || "");
    const projectChanged = prevProjectId !== effectiveProjectId;
    const prevCompletedNormalized = formatDateForInput(editing.completed_at);
    const completedChanged = (formCompletedAt || "") !== (prevCompletedNormalized || "");
    return (
      titleChanged ||
      descriptionChanged ||
      priorityChanged ||
      disciplineChanged ||
      dueChanged ||
      labelsChanged ||
      typeChanged ||
      statusChanged ||
        projectChanged ||
      completedChanged
    );
  }, [
    editing,
    formTitle,
    formDescription,
    formPriority,
    formDiscipline,
    formDueDate,
    formLabels,
    formType,
    formStatus,
    formProjectId,
    formIsMiscScoped,
    formCompletedAt,
    miscProjectId,
  ]);

  const inlineDirty =
    Boolean(newTitle.trim()) ||
    Boolean(newDescription.trim()) ||
    Boolean(newDueBy) ||
    Boolean(newPriority) ||
    (newDisciplineModified && Boolean(newDiscipline));
  const dirty = inlineDirty || editHasChanges;

  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  useEffect(
    () => () => {
      onDirtyChange?.(false);
    },
    [onDirtyChange],
  );

  useEffect(() => {
    // Wait for user to be defined (Clerk loaded)
    if (user === undefined) {
      console.debug("[TasksPanel] waiting for auth…");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await listTasks(user);
        const raw = Array.isArray(data) ? data : [];
        const normalized = raw.map((t) => ({
          ...t,
          status: normalizeStatus((t as any).status),
        }));
        if (isDemo) {
          const snap = readDemoSnapshot();
          if (snap) {
            setTasks(snap);
          } else {
            setTasks(normalized);
          }
        } else {
          setTasks(normalized);
        }
        // Heuristic: detect server status convention (legacy todo/doing/done vs new pending/in-progress/complete)
        const anyOld = raw.some((t: any) => ["todo","doing","done"].includes(t?.status));
        const anyNew = raw.some((t: any) => ["pending","in-progress","complete","coordinating"].includes(t?.status));
        if (anyOld && !anyNew) setUseLegacyStatus(true);
        else if (anyNew && !anyOld) setUseLegacyStatus(false);
        // else ambiguous: preserve current default (new scheme)
      } catch (e: any) {
        console.error("[TasksPanel] load error", e);
        toast({ title: "Failed to load tasks", description: e?.message || "", variant: "destructive" });
      } finally {
        setLoading(false);
      }
    })();
  }, [user, isDemo, readDemoSnapshot]);

  function shortUserId(id?: string | null): string {
    const s = String(id || "").trim();
    if (!s) return "unknown";
    if (s.length <= 12) return s;
    return `${s.slice(0, 6)}…${s.slice(-4)}`;
  }

  function openShareDialog(t: Task) {
    if (isDemo) {
      toast({ title: "Demo mode", description: "Sharing is disabled for this demo.", variant: "default" });
      return;
    }
    setShareTask(t);
    setShareRecipient("");
    setSharePermission("read");
    setShareError(null);
    setShareIncludeRevoked(false);
    setShareList(null);
    setShareMembers([]);
    setShareOpen(true);
  }

  async function handleDismissShare(t: Task) {
    if (isDemo) {
      toast({ title: "Demo mode", description: "Sharing is disabled for this demo.", variant: "default" });
      return;
    }
    const shareId = t.access?.share_id;
    if (!shareId) return;
    try {
      await dismissTaskShare(t.id, shareId, user as any);
      setTasks((prev) => prev.filter((x) => x.id !== t.id));
      toast({ title: "Dismissed", description: "This shared task will no longer appear in your list." });
    } catch (e: any) {
      toast({ title: "Dismiss failed", description: e?.message || "", variant: "destructive" });
    }
  }

  useEffect(() => {
    const t = shareTask;
    if (!shareOpen || !t) return;
    let cancelled = false;
    setShareLoading(true);
    setShareError(null);
    (async () => {
      try {
        // Always load company members for sharing — any company member is a valid recipient.
        let members: Array<CompanyMember | ProjectMember> = [];
        if (t.company_id) {
          members = await listCompanyMembers(t.company_id, user as any);
        } else {
          setShareError("Sharing is unavailable for this item (missing company scope).");
          members = [];
        }

        const currentUserId = user?.id || "";
        const options = (members || [])
          .filter((m: any) => (m?.user_id || "") !== currentUserId)
          .map((m: any) => {
            const profile = m?.profile || null;
            const name = (profile?.display_name || "").trim();
            const email = (profile?.email || "").trim();
            const label = name ? (email ? `${name} • ${email}` : name) : email ? email : m.user_id;
            return { user_id: m.user_id as string, label };
          })
          .sort((a, b) => a.label.localeCompare(b.label));

        const shares = await listTaskShares(t.id, { includeRevoked: shareIncludeRevoked }, user as any);

        if (cancelled) return;
        setShareMembers(options);
        setShareList(shares);
        if (!shareRecipient && options.length > 0) {
          setShareRecipient(options[0].user_id);
        }
      } catch (e: any) {
        if (!cancelled) setShareError(e?.message || "Failed to load sharing data");
      } finally {
        if (!cancelled) setShareLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shareOpen, shareTask?.id, shareIncludeRevoked, user?.id]);

  async function submitShare() {
    if (!shareTask) return;
    if (!shareRecipient) return;
    try {
      await createTaskShare(shareTask.id, { principal_user_id: shareRecipient, permission: sharePermission }, user as any);
      const shares = await listTaskShares(shareTask.id, { includeRevoked: shareIncludeRevoked }, user as any);
      setShareList(shares);
      toast({
        title: "Shared",
        description: "If you need to change permission, revoke and re-share (backend is idempotent per recipient).",
      });
    } catch (e: any) {
      toast({ title: "Share failed", description: e?.message || "", variant: "destructive" });
    }
  }

  async function handleRevokeShare(shareId: string) {
    if (!shareTask) return;
    try {
      await revokeTaskShare(shareTask.id, shareId, user as any);
      const shares = await listTaskShares(shareTask.id, { includeRevoked: shareIncludeRevoked }, user as any);
      setShareList(shares);
      toast({ title: "Revoked", description: "Share revoked." });
    } catch (e: any) {
      toast({ title: "Revoke failed", description: e?.message || "", variant: "destructive" });
    }
  }

  // Load pinned tasks once user is known
  useEffect(() => {
    if (user === undefined) return;
    const userId = user?.id;
    setPinnedTaskIds(loadPinnedIds(userId, "task"));
  }, [user]);

  useEffect(() => {
    if (user === undefined) {
      return;
    }
    if (!user?.id) {
      setProjects([]);
      setProjectsError(null);
      setProjectsLoading(false);
      return;
    }
    let cancelled = false;
    setProjectsLoading(true);
    setProjectsError(null);
    (async () => {
      try {
        const projectData = await listProjects({ include_misc: true }, user);
        if (cancelled) return;
        setProjects(Array.isArray(projectData) ? projectData : []);
      } catch (e: any) {
        console.error("[TasksPanel] project load error", e);
        if (!cancelled) {
          setProjects([]);
          setProjectsError("Failed to load projects");
        }
      } finally {
        if (!cancelled) setProjectsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const handler = (event: Event) => {
      const project = (event as CustomEvent<{ project?: ProjectSummary }>).detail?.project;
      if (!project?.id) return;
      setProjects((prev) => {
        const exists = prev.some((p) => p.id === project.id);
        if (exists) {
          return prev.map((p) => (p.id === project.id ? { ...p, ...project } : p));
        }
        return [...prev, project];
      });
      setProjectsError(null);
      setNewProjectId(project.id);
    };
    window.addEventListener("workspace:project-created", handler as EventListener);
    return () => window.removeEventListener("workspace:project-created", handler as EventListener);
  }, []);

  useEffect(() => {
    if (!editing) return;
    if (formStatus === "complete") {
      if (!formCompletedAt) {
        const existing = formatDateForInput(editing.completed_at);
        const fallback = existing || new Date().toISOString().slice(0, 10);
        setFormCompletedAt(fallback);
      }
    } else if (formCompletedAt) {
      setFormCompletedAt("");
    }
  }, [editing, formStatus, formCompletedAt]);

  function getPriorityRank(p?: TaskPriority | null): number {
    if (!p) return Number.POSITIVE_INFINITY;
    if (p === 'low') return 0;
    if (p === 'medium') return 1;
    if (p === 'high') return 2;
    if (p === 'urgent') return 3;
    return Number.POSITIVE_INFINITY;
  }

  function getTypeLabel(t: Task): string {
    if (t.task_type) return t.task_type;
    return (t.labels && t.labels[0]) ? String(t.labels[0]) : "";
  }

  function compareStrings(a?: string | null, b?: string | null): number {
    const aa = (a || "").toLowerCase();
    const bb = (b || "").toLowerCase();
    if (!aa && !bb) return 0;
    if (!aa) return 1; // nulls last
    if (!bb) return -1;
    return aa.localeCompare(bb);
  }

  function compareDates(a?: string | null, b?: string | null): number {
    const da = parseLocalDateOnly(a);
    const db = parseLocalDateOnly(b);
    const ta = da ? da.getTime() : Number.POSITIVE_INFINITY; // nulls last
    const tb = db ? db.getTime() : Number.POSITIVE_INFINITY;
    if (ta === tb) return 0;
    return ta < tb ? -1 : 1;
  }

  function compareByKey(a: Task, b: Task, key: SortKey): number {
    switch (key) {
      case 'type':
        return compareStrings(getTypeLabel(a), getTypeLabel(b));
      case 'title':
        return compareStrings(a.title, b.title);
      case 'discipline':
        return compareStrings((a as any).discipline, (b as any).discipline);
      case 'priority': {
        const pa = getPriorityRank(a.priority || null);
        const pb = getPriorityRank(b.priority || null);
        if (pa === pb) return 0;
        return pa < pb ? -1 : 1;
      }
      case 'due_date':
        return compareDates(a.due_date || null, b.due_date || null);
      default:
        return 0;
    }
  }

  function compare(a: Task, b: Task): number {
    if (sort.key) {
      const base = compareByKey(a, b, sort.key);
      return sort.dir === 'asc' ? base : -base;
    }
    // default: created_at desc (newest first)
    const ac = a.created_at || "";
    const bc = b.created_at || "";
    return bc.localeCompare(ac);
  }

  function toggleSort(key: SortKey) {
    setSort((prev) => {
      if (prev.key !== key) return { key, dir: 'asc' };
      if (prev.dir === 'asc') return { key, dir: 'desc' };
      return { key: null, dir: 'asc' };
    });
  }

  function matchesTaskDateFilter(t: Task): boolean {
    if (!dateFilterField && !dateFilterStart && !dateFilterEnd) return true;
    const fieldValue = dateFilterField === "completed" ? t.completed_at : t.due_date;
    if (!fieldValue) {
      // If user is filtering by a range, tasks without that field should be excluded
      if (dateFilterStart || dateFilterEnd) return false;
      return true;
    }
    const taskDate = parseLocalDateOnly(fieldValue);
    if (!taskDate) return false;
    const ts = taskDate.getTime();
    const startDate = dateFilterStart ? startOfDay(parseFilterDate(dateFilterStart) || new Date(NaN)) : null;
    const endDate = dateFilterEnd ? endOfDay(parseFilterDate(dateFilterEnd) || new Date(NaN)) : null;
    if (startDate && isNaN(startDate.getTime())) return false;
    if (endDate && isNaN(endDate.getTime())) return false;
    if (startDate && ts < startDate.getTime()) return false;
    if (endDate && ts > endDate.getTime()) return false;
    return true;
  }

  function clearAllFilters() {
    setStatusFilter("all");
    setTypeFilter("all");
    setDisciplineFilter("all");
    setPriorityFilter("all");
    setDateFilterField(null);
    setDateFilterStart("");
    setDateFilterEnd("");
    setProjectClientFilter("all");
    setProjectLocationFilter("all");
    setProjectFilter("all");
  }

  const hasActiveFilters =
    projectFilter !== "all" ||
    projectClientFilter !== "all" ||
    projectLocationFilter !== "all" ||
    statusFilter !== "all" ||
    typeFilter !== "all" ||
    disciplineFilter !== "all" ||
    priorityFilter !== "all" ||
    !!dateFilterField ||
    !!dateFilterStart ||
    !!dateFilterEnd;

  const activeTasks = useMemo(() => {
    let arr = (Array.isArray(tasks) ? tasks : []).filter((t) => normalizeStatus(t.status) !== 'complete');
    // Project filter (applies regardless of origin scope)
    if (projectFilter === "__misc__") {
      arr = miscProjectId ? arr.filter((t) => (t.project_id ?? null) === miscProjectId) : [];
    } else if (projectFilter !== "all") {
      arr = arr.filter((t) => (t.project_id ?? null) === projectFilter);
    } else if (projectClientFilter !== "all" || projectLocationFilter !== "all") {
      arr = arr.filter((t) => {
        if (!t.project_id) return false;
        const meta = projectById.get(t.project_id);
        if (!meta) return false;
        if (projectClientFilter !== "all" && normalizeClientKey(meta) !== projectClientFilter) return false;
        if (projectLocationFilter !== "all" && normalizeLocationKey(meta) !== projectLocationFilter) return false;
        return true;
      });
    }
    if (statusFilter !== "all") {
      arr = arr.filter((t) => normalizeStatus(t.status) === statusFilter);
    }
    if (typeFilter !== "all") {
      arr = arr.filter((t) => {
        const lbl = getTypeLabel(t);
        return lbl && lbl.toLowerCase() === typeFilter.toLowerCase();
      });
    }
    if (disciplineFilter !== "all") {
      arr = arr.filter((t) => (t.discipline ?? null) === disciplineFilter);
    }
    if (priorityFilter !== "all") {
      arr = arr.filter((t) => (t.priority ?? null) === priorityFilter);
    }
    arr = arr.filter((t) => matchesTaskDateFilter(t));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      arr = arr.filter((t) =>
        (t.title || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
      );
    }
    const sorted = arr.sort(compare);
    const pinned = sorted.filter((t) => isPinned(pinnedTaskIds, t.id));
    const unpinned = sorted.filter((t) => !isPinned(pinnedTaskIds, t.id));
    return [...pinned, ...unpinned];
  }, [
    tasks,
    search,
    sort,
    pinnedTaskIds,
    projectById,
    projectFilter,
    projectClientFilter,
    projectLocationFilter,
    statusFilter,
    typeFilter,
    disciplineFilter,
    priorityFilter,
    dateFilterField,
    dateFilterStart,
    dateFilterEnd,
    miscProjectId,
  ]);

  const completedTasks = useMemo(() => {
    let arr = (Array.isArray(tasks) ? tasks : []).filter((t) => normalizeStatus(t.status) === 'complete');
    if (projectFilter === "__misc__") {
      arr = miscProjectId ? arr.filter((t) => (t.project_id ?? null) === miscProjectId) : [];
    } else if (projectFilter !== "all") {
      arr = arr.filter((t) => (t.project_id ?? null) === projectFilter);
    } else if (projectClientFilter !== "all" || projectLocationFilter !== "all") {
      arr = arr.filter((t) => {
        if (!t.project_id) return false;
        const meta = projectById.get(t.project_id);
        if (!meta) return false;
        if (projectClientFilter !== "all" && normalizeClientKey(meta) !== projectClientFilter) return false;
        if (projectLocationFilter !== "all" && normalizeLocationKey(meta) !== projectLocationFilter) return false;
        return true;
      });
    }
    if (statusFilter !== "all") {
      arr = arr.filter((t) => normalizeStatus(t.status) === statusFilter);
    }
    if (typeFilter !== "all") {
      arr = arr.filter((t) => {
        const lbl = getTypeLabel(t);
        return lbl && lbl.toLowerCase() === typeFilter.toLowerCase();
      });
    }
    if (disciplineFilter !== "all") {
      arr = arr.filter((t) => (t.discipline ?? null) === disciplineFilter);
    }
    if (priorityFilter !== "all") {
      arr = arr.filter((t) => (t.priority ?? null) === priorityFilter);
    }
    arr = arr.filter((t) => matchesTaskDateFilter(t));
    if (searchCompleted.trim()) {
      const q = searchCompleted.trim().toLowerCase();
      arr = arr.filter((t) =>
        (t.title || "").toLowerCase().includes(q) || (t.description || "").toLowerCase().includes(q)
      );
    }
    const sorted = arr.sort(compare);
    const pinned = sorted.filter((t) => isPinned(pinnedTaskIds, t.id));
    const unpinned = sorted.filter((t) => !isPinned(pinnedTaskIds, t.id));
    return [...pinned, ...unpinned];
  }, [
    tasks,
    searchCompleted,
    sort,
    pinnedTaskIds,
    projectById,
    projectFilter,
    projectClientFilter,
    projectLocationFilter,
    statusFilter,
    typeFilter,
    disciplineFilter,
    priorityFilter,
    dateFilterField,
    dateFilterStart,
    dateFilterEnd,
    miscProjectId,
  ]);

  async function handleCreateInline() {
    const title = newTitle.trim();
    if (!title) return;
    const labels: string[] | null = newType ? [newType] : null;
    const isoDue = newDueBy ? newDueBy : null;
    const selectedProject = newProjectId ? projectById.get(newProjectId) : undefined;
    const input: CreateTaskInput = {
      title,
      description: newDescription.trim() || null,
      due_date: isoDue,
      priority: (newPriority || null) as TaskPriority | null,
      discipline: (newDiscipline || null) as TaskDiscipline | null,
      task_type: newType || null,
      labels,
    };
    if (selectedProject) {
      input.project_id = selectedProject.id;
      input.project_name = selectedProject.project_name ?? null;
    } else if (isMiscProject) {
      input.project_id = miscProjectId;
      input.project_name = miscProject?.project_name ?? "Misc";
    }
    const temp: Task = {
      id: `temp:${Math.random().toString(36).slice(2)}`,
      user_id: "me",
      ...input,
      status: "pending",
      task_type: newType,
      project_id: selectedProject?.id ?? (isMiscProject ? miscProjectId : null),
      project_name: selectedProject?.project_name ?? (isMiscProject ? (miscProject?.project_name ?? "Misc") : null),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    } as Task;
    setTasks((prev) => {
      const next = [temp, ...prev];
      persistDemoSnapshot(next);
      return next;
    });
    setNewTitle("");
    setNewDescription("");
    setNewDueBy("");
    setNewPriority("");
    setNewProjectId("");
    setNewDisciplineModified(false);
    if (isDemo) {
      toast({ title: "Saved locally", description: "This task will not be sent to the backend in demo mode." });
      return;
    }
    try {
      const serverStatus: any = useLegacyStatus ? "todo" : "pending";
      const serverInput: any = { ...input, status: serverStatus };
      const saved = await createTask(serverInput, user);
      const savedNorm: Task = { ...(saved as any), status: normalizeStatus((saved as any)?.status) } as Task;
      setTasks((prev) => {
        const next = prev.map((t) => (t.id === temp.id ? savedNorm : t));
        persistDemoSnapshot(next);
        return next;
      });
    } catch (e: any) {
      console.error("[TasksPanel] create error", e);
      setTasks((prev) => {
        const next = prev.filter((t) => t.id !== temp.id);
        persistDemoSnapshot(next);
        return next;
      });
      const msg = String(e?.message || "");
      const hint =
        /project not found/i.test(msg)
          ? "You may not be a member of that project. Ask a project admin to add you, or create the task in Misc."
          : "";
      toast({ title: "Create failed", description: hint ? `${msg}\n\n${hint}` : msg, variant: "destructive" });
    }
  }

  function handleFormProjectChange(value: string) {
    setFormProjectId(value);
  }

  function openEditModal(t: Task) {
    setEditing(t);
    setFormTitle(t.title || "");
    setFormDescription(t.description || "");
    setFormStatus(normalizeStatus(t.status));
    setFormPriority(t.priority || "");
    setFormDiscipline((t as any).discipline || "");
    setFormDueDate(formatDateForInput(t.due_date));
    const inferredType = (t.task_type || (t.labels?.[0] ?? "") || "").toString();
    const matched = TYPE_OPTIONS.find((opt) => opt.toLowerCase() === inferredType.toLowerCase()) || "";
    setFormType(matched);
    const rest = (t.labels || []).filter((label) => label && label.toLowerCase() !== matched.toLowerCase());
    setFormLabels(rest.join(", "));
    if (t.project_id) {
      const selection = selectionFromProjectId(projects, t.project_id);
      if (selection) {
        setFormClientKey(selection.clientKey);
        setFormLocationKey(selection.locationKey);
      }
    } else {
      setFormClientKey("");
      setFormLocationKey("");
    }
    setFormProjectId(t.project_id ?? "");
    setFormCompletedAt(formatDateForInput(t.completed_at));
    setFormIsMiscScoped(isMiscProjectId(t.project_id ?? null, miscProjectId));
  }

  async function handleSaveEdit() {
    if (!editing) return;
    const labels = formLabels.trim() ? formLabels.split(",").map((l) => l.trim()).filter(Boolean) : null;
    const isoFormDue = formDueDate ? formDueDate : null;
    // Build minimal change set: only send fields that changed
    const changes: UpdateTaskInput = {};
    const trimmedTitle = formTitle.trim();
    if (trimmedTitle && trimmedTitle !== (editing.title || "")) (changes as any).title = trimmedTitle;
    const nextDescription = formDescription.trim() || null;
    if (nextDescription !== (editing.description ?? null)) changes.description = nextDescription;
    const nextPriority = (formPriority || null) as TaskPriority | null;
    if (nextPriority !== (editing.priority ?? null)) changes.priority = nextPriority as any;
    const nextDiscipline = (formDiscipline || null) as TaskDiscipline | null;
    if (nextDiscipline !== (editing.discipline ?? null)) (changes as any).discipline = nextDiscipline;
    const prevDueNormalized = formatDateForInput(editing.due_date);
    if ((isoFormDue || "") !== (prevDueNormalized || "")) changes.due_date = isoFormDue;
    // Rebuild labels as [type, ...other]
    const normalizedType = (formType || "").toString().trim();
    const extra = (labels || []).filter((l) => l.toLowerCase() !== normalizedType.toLowerCase());
    const combined = normalizedType ? [normalizedType, ...extra].filter(Boolean) : extra;
    const nextLabels = combined.length ? combined : null;
    const prevLabelsJson = JSON.stringify(editing.labels ?? null);
    const nextLabelsJson = JSON.stringify(nextLabels);
    if (nextLabelsJson !== prevLabelsJson) changes.labels = nextLabels as any;
    const prevTypeRaw = (editing.task_type ?? (editing.labels?.[0] ?? "") ?? "").toString().trim();
    if (prevTypeRaw.toLowerCase() !== normalizedType.toLowerCase()) {
      (changes as any).task_type = normalizedType ? (normalizedType as TaskType) : null;
    }
    const selectedProject = formIsMiscScoped ? miscProject ?? undefined : (formProjectId ? projectById.get(formProjectId) : undefined);
    const desiredProjectId = formIsMiscScoped ? (miscProjectId ?? null) : (formProjectId ? formProjectId : null);
    let desiredProjectName: string | null | undefined;
    if (formIsMiscScoped) {
      desiredProjectName = miscProject?.project_name ?? "Misc";
    } else if (desiredProjectId) {
      desiredProjectName = selectedProject?.project_name ?? editing.project_name ?? null;
    } else {
      desiredProjectName = undefined;
    }
    if ((editing.project_id ?? null) !== desiredProjectId) {
      (changes as any).project_id = desiredProjectId;
    }
    if (desiredProjectName !== undefined && (editing.project_name ?? null) !== desiredProjectName) {
      (changes as any).project_name = desiredProjectName;
    }
    const prevCompletedNormalized = formatDateForInput(editing.completed_at);
    const desiredCompleted = formCompletedAt ? formCompletedAt : null;
    if ((desiredCompleted || "") !== (prevCompletedNormalized || "")) (changes as any).completed_at = desiredCompleted;
    const serverStatus: any = (() => {
      if (useLegacyStatus) {
        if (formStatus === "pending") return "todo";
        if (formStatus === "in_progress" || formStatus === "coordinating") return "doing";
        return "done";
      }
      if (formStatus === "in_progress") return "in-progress";
      return formStatus;
    })();
    const prevUiStatus = normalizeStatus(editing.status);
    const statusChanged = prevUiStatus !== formStatus;
    const serverChanges: any = { ...changes };
    if (statusChanged) serverChanges.status = serverStatus;
    if (formStatus === "complete" && !serverChanges.completed_at && !formCompletedAt) {
      const today = new Date().toISOString().slice(0, 10);
      serverChanges.completed_at = today;
      (changes as any).completed_at = today;
    }
    // If nothing changed, bail early
    if (Object.keys(serverChanges).length === 0) {
      setEditing(null);
      toast({ title: "No changes to save", description: "No fields were modified.", variant: "default" });
      return;
    }
    const prev = tasks;
    const updated = {
      ...editing,
      ...changes,
      ...(statusChanged ? { status: formStatus } : {}),
      ...(changes.labels !== undefined ? { labels: changes.labels || null } : {}),
      ...(changes.task_type !== undefined ? { task_type: (changes as any).task_type ?? null } : {}),
      ...(changes.project_id !== undefined ? { project_id: (changes as any).project_id ?? null } : {}),
      ...(changes.project_name !== undefined ? { project_name: (changes as any).project_name ?? null } : {}),
      ...(changes.completed_at !== undefined ? { completed_at: (changes as any).completed_at ?? null } : {}),
      updated_at: new Date().toISOString(),
    } as Task;
    setTasks((arr) => {
      const next = arr.map((t) => (t.id === editing.id ? updated : t));
      persistDemoSnapshot(next);
      return next;
    });
    setEditing(null);
    try {
      if (isDemo) return;
      await updateTask(editing.id, serverChanges, user as any);
    } catch (e: any) {
      console.error("[TasksPanel] update error", e);
      setTasks(prev);
      toast({ title: "Update failed", description: e?.message || "", variant: "destructive" });
    }
  }

  async function handleCreateInlineFromForm() {
  const title = formTitle.trim();
  if (!title) return;

  const labels = formLabels
    ? formLabels.split(",").map((l) => l.trim()).filter(Boolean)
    : null;

  const input: CreateTaskInput = {
    title,
    description: formDescription || null,
    due_date: formDueDate || null,
    priority: formPriority || null,
    discipline: formDiscipline || null,
    task_type: formType || null,
    labels,
    project_id: formIsMiscScoped ? (miscProjectId ?? null) : (formProjectId || null),
    ...(formIsMiscScoped ? { project_name: miscProject?.project_name ?? "Misc" } : {}),
  };

  await createTask(input, user);

  setEditing(null);

  // reload tasks
  const data = await listTasks(user);
  setTasks(data);
}


  async function handleStatus(t: Task, status: TaskStatus) {
    const prev = tasks;
    const completionTimestamp = status === "complete" ? new Date().toISOString() : null;
    const next = tasks.map((x) =>
      x.id === t.id
        ? { ...x, status, completed_at: completionTimestamp, updated_at: new Date().toISOString() }
        : x,
    );
    setTasks(next);
    persistDemoSnapshot(next);
    try {
      if (isDemo) return;
      const serverStatus = (() => {
        if (useLegacyStatus) {
          if (status === "pending") return "todo";
          if (status === "in_progress" || status === "coordinating") return "doing";
          return "done";
        }
        if (status === "in_progress") return "in-progress";
        return status;
      })();
      const payload: any = { status: serverStatus };
      if (completionTimestamp) {
        payload.completed_at = completionTimestamp;
      } else if (t.completed_at) {
        payload.completed_at = null;
      }
      await updateTask(t.id, payload, user as any);
    } catch (e: any) {
      console.error("[TasksPanel] update error", e);
      setTasks(prev);
      persistDemoSnapshot(prev);
      toast({ title: "Update failed", description: e?.message || "", variant: "destructive" });
    }
  }

  function handleDelete(t: Task) {
    const isOwner = !!user?.id && t.user_id === user.id;
    if (!isOwner && t.access?.share_id) {
      return handleDismissShare(t);
    }
    setConfirmDeleteTask(t);
  }

  async function executeDeleteTask(t: Task) {
    const prev = tasks;
    setTasks((arr) => {
      const next = arr.filter((x) => x.id !== t.id);
      persistDemoSnapshot(next);
      return next;
    });
    try {
      if (isDemo) return;
      await deleteTask(t.id, user);
    } catch (e: any) {
      console.error("[TasksPanel] delete error", e);
      setTasks(prev);
      persistDemoSnapshot(prev);
      toast({ title: "Delete failed", description: e?.message || "", variant: "destructive" });
    }
  }

  function handleTogglePin(id: string) {
    const userId = user?.id;
    setPinnedTaskIds((prev) => setPinned(userId, "task", id, !prev.has(id)));
  }

  const scopeInfo = useMemo(() => {
    if (projectId) {
      const meta = projectById.get(projectId);
      const name = meta?.project_name || projectName || "Project";
      const client = meta?.client_name;
      return {
        label: client ? `Project: ${name} • ${client}` : `Project: ${name}`,
        description: "Window is scoped to this project.",
      };
    }
    if (isMiscProject) {
      return {
        label: "Window: Misc bucket",
        description: "Window is scoped to tasks in the Misc project.",
      };
    }
    return {
      label: "Window: All tasks",
      description: "Use Filters to narrow by project; ALL shows project and non-project tasks.",
    };
  }, [projectById, projectId, projectName, isMiscProject]);

  // Best-effort: resolve creator user_id -> display_name using project/company member endpoints.
  // This avoids showing raw Clerk IDs in the UI.
  useEffect(() => {
    if (user === undefined) return;
    let cancelled = false;

    const creatorIds = new Set<string>();
    const projectIds = new Set<string>();
    const companyIds = new Set<string>();
    (tasks || []).forEach((t) => {
      const creatorId = String(t.created_by || t.user_id || "").trim();
      if (creatorId) creatorIds.add(creatorId);
      if (t.project_id) projectIds.add(t.project_id);
      if (!t.project_id && t.company_id) companyIds.add(t.company_id);
    });

    const missingProjects = Array.from(projectIds).filter((id) => !loadedProjectMembersRef.current.has(id));
    const missingCompanies = Array.from(companyIds).filter((id) => !loadedCompanyMembersRef.current.has(id));
    if (missingProjects.length === 0 && missingCompanies.length === 0) return;

    (async () => {
      try {
        const results = await Promise.all([
          ...missingProjects.map(async (projectId) => {
            const members = await listProjectMembers(projectId, user as any);
            return { kind: "project" as const, id: projectId, members };
          }),
          ...missingCompanies.map(async (companyId) => {
            const members = await listCompanyMembers(companyId, user as any);
            return { kind: "company" as const, id: companyId, members };
          }),
        ]);
        if (cancelled) return;

        const next: Record<string, string> = {};
        for (const r of results) {
          if (r.kind === "project") loadedProjectMembersRef.current.add(r.id);
          if (r.kind === "company") loadedCompanyMembersRef.current.add(r.id);
          for (const m of (r as any).members || []) {
            const userId = String(m?.user_id || "").trim();
            if (!userId) continue;
            const profile = m?.profile || null;
            const display = String(profile?.display_name || "").trim();
            const email = String(profile?.email || "").trim();
            const label = display || email;
            if (label) next[userId] = label;
          }
        }

        // Only keep names we might actually need (avoid unbounded growth)
        const filtered: Record<string, string> = {};
        Object.entries(next).forEach(([id, name]) => {
          if (creatorIds.has(id)) filtered[id] = name;
        });

        if (Object.keys(filtered).length) {
          setUserDisplayNames((prev) => ({ ...prev, ...filtered }));
        }
      } catch (e: any) {
        // Best-effort; creator will fall back to "You" or short id.
        console.debug("[TasksPanel] failed to resolve creator display names", e?.message || e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [tasks, user]);

  function getCreatorLabel(t: Task): string {
    const creatorId = String(t.created_by || t.user_id || "").trim();
    const label = creatorId ? userDisplayNames[creatorId] : "";
    if (label) return label;
    if (user?.id && creatorId && creatorId === user.id) return "You";
    if (creatorId) {
      if (!loggedUnknownCreatorRef.current.has(creatorId)) {
        loggedUnknownCreatorRef.current.add(creatorId);
        console.debug("[TasksPanel] creator display_name not resolved", { creatorId });
      }
      return shortUserId(creatorId);
    }
    return "Unknown";
  }

  function getAccessLabel(t: Task, isOwner: boolean): "read" | "read/write" | "owner" {
    if (isOwner) return "owner";
    if (t.access?.kind === "shared") {
      return t.access.permission === "write" ? "read/write" : "read";
    }
    // RBAC/unscoped access: treat as editable access in UI (matches current capabilities)
    return "read/write";
  }

  function FieldLabel({ children }: { children: string }) {
  return (
    <span className="text-[15px] uppercase tracking-wide text-purple-300">
      {children}
    </span>
  );
}


  return (
    <div className="space-y-1 px-3 py-2" >

      {/* Task Toolbar */}
      <div className="relative z-10 flex items-center gap-3 pb-1 w-full min-w-0 mt-2">
        <button
                type="button"
                onClick={() => {
                  setEditing({} as Task); // open the big modal

                  setFormTitle("");
                  setFormDescription("");
                  setFormStatus("pending");
                  setFormPriority("");
                  setFormDiscipline("");
                  setFormDueDate("");
                  setFormLabels("");
                  setFormType(TYPE_OPTIONS[0]);
                  setFormClientKey("");
                  setFormLocationKey("");
                  setFormProjectId("");
                  setFormIsMiscScoped(!!isMiscProject);
                }}
                className={`
                  px-4 py-1.5
                  text-[14px] text-white
                  rounded-2xl
                  transition-all

                  bg-purple-700/40
                  ring-1 ring-[#7b2ee6]/80

                  hover:bg-purple-500/40
                  hover:ring-[#7b2ee6]/60
                `}
              >
                Create Task
              </button>
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search..."
          className="
            flex-1
            text-[14px]
            px-4 py-1.5
            rounded-2xl
            bg-black/40
            ring-1 ring-[#7b2ee6]/40
            text-white
            focus:outline-none
            focus:ring-[#7b2ee6]/70
            transition-all
          "
        />

        {/* Task Filter Button */}
        <button
          onClick={() => setFiltersOpen(true)}
          className={`
            px-4 py-1.5
            text-[14px] font-medium
            flex items-center gap-2
            rounded-3xl
            transition-all

            ${hasActiveFilters
              ? `
                bg-purple-700/20
                ring-2 ring-[#7b2ee6]
                text-white
                shadow-[0_0_25px_rgba(123,46,230,0.45)]
              `
              : `
                ${panelClass}
                text-white/80
                hover:bg-purple-700/10
                hover:ring-[#7b2ee6]/60
              `}
          `}
        >
          <Filter className="h-3 w-3" />
          <span>Filters</span>
        </button>
      </div>

      {/* Cards (Active) */}
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : (
        <div className="-mx-3 px-4 py-2">
          <TaskGrid
            tasks={activeTasks}
            projectById={projectById}
            getCreatorLabel={getCreatorLabel}
            onStatus={handleStatus}
            onPin={handleTogglePin}
            onEdit={openEditModal}
            onDelete={handleDelete}
            getStatusBadge={getStatusBadge}
            getStatusLabel={getStatusLabel}
            onRowClick={(t) => setSelectedTask(t)}
            pinnedIds={pinnedTaskIds}
            onShare={openShareDialog}
          />
        </div>
      )}

      {/* Completed Tasks */}
      <div>
        <Collapsible open={isCompletedOpen} onOpenChange={setIsCompletedOpen}>
          <CollapsibleTrigger className="w-full text-left text-sm font-semibold rounded-md border border-emerald-500/25 bg-emerald-950/35 px-3 py-2 text-emerald-100 transition-colors hover:bg-emerald-900/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/20">
            Completed Tasks {isCompletedOpen ? "▲" : "▼"}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="flex items-center gap-2 mt-2 mb-2">
              <input
                value={searchCompleted}
                onChange={(e) => setSearchCompleted(e.target.value)}
                placeholder="Search completed"
                className="flex-1 text-xs border rounded px-2 py-0.5 bg-transparent"
              />
            </div>
            <div className="space-y-2">
              <TaskGrid
                tasks={completedTasks}
                projectById={projectById}
                getCreatorLabel={getCreatorLabel}
                onStatus={handleStatus}
                onPin={handleTogglePin}
                onEdit={openEditModal}
                onDelete={handleDelete}
                getStatusBadge={getStatusBadge}
                getStatusLabel={getStatusLabel}
                pinnedIds={pinnedTaskIds}
                onRowClick={(t) => setSelectedTask(t)}
              />
            </div>
          </CollapsibleContent>
        </Collapsible>
      </div>

      {/* Task Share Modal */}
      <Dialog
        open={!!shareTask}
        onOpenChange={(open) => {
          if (!open) {
            setShareTask(null);
            setShareError(null);
          }
        }}
      >
      <DialogContent className="max-w-[min(680px,92vw)] p-0 border-0 bg-transparent shadow-none [&>button]:hidden
">
        <ModalPanel className="p-6">
          {shareTask ? (
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between pb-3 border-b border-purple-500/10">
                <div className="space-y-1">
                  <div className="text-lg font-semibold text-foreground">
                    Manage shares
                  </div>
                  <div className="text-sm text-muted-foreground">
                    Manage who has access to this task and their permissions.
                  </div>
                  <div className="text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Task:</span>{" "}
                    {shareTask.title || "Untitled"}
                  </div>
                </div>

              <CloseButton onClick={() => setShareTask(null)} />
              </div>

              {shareError ? (
                <div className="text-xs text-destructive">{shareError}</div>
              ) : null}

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 items-end">
                <div className="sm:col-span-2">
                  <label className="text-[11px] text-muted-foreground">
                    Recipient
                  </label>
                  <select
                    className="w-full text-sm border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                    value={shareRecipient}
                    onChange={(e) => setShareRecipient(e.target.value)}
                    disabled={
                      shareLoading || !!shareError || shareMembers.length === 0
                    }
                  >
                    <option value="">
                      {shareLoading
                        ? "Loading…"
                        : shareMembers.length
                        ? "Select a user…"
                        : "No eligible members"}
                    </option>
                    {shareMembers.map((m) => (
                      <option key={m.user_id} value={m.user_id}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-[11px] text-muted-foreground">
                    Permission
                  </label>
                  <select
                    className="w-full text-sm border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                    value={sharePermission}
                    onChange={(e) =>
                      setSharePermission(e.target.value as SharePermission)
                    }
                    disabled={shareLoading || !!shareError}
                  >
                    <option value="read">Read</option>
                    <option value="write">Read / Write</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={shareIncludeRevoked}
                    onChange={(e) =>
                      setShareIncludeRevoked(e.target.checked)
                    }
                    className="h-3 w-3"
                  />
                  Show revoked
                </label>

                <Button
                  onClick={submitShare}
                  disabled={
                    shareLoading || !shareRecipient || !!shareError
                  }
                >
                  Share
                </Button>
              </div>

              <div className="rounded-md border border-border bg-window/60 p-2">
                <div className="text-xs font-semibold mb-2">
                  Existing shares
                </div>

                {!shareList ? (
                  <div className="text-xs text-muted-foreground">
                    {shareLoading ? "Loading…" : "No data"}
                  </div>
                ) : shareList.shares.length === 0 ? (
                  <div className="text-xs text-muted-foreground">
                    No shares yet.
                  </div>
                ) : (
                  <div className="space-y-1">
                    {shareList.shares.map((s) => {
                      const profile =
                        shareList.profiles.find(
                          (p) => p.user_id === s.principal_user_id
                        ) || null;

                      const recipientLabel =
                        (profile?.display_name || "").trim() ||
                        (profile?.email || "").trim() ||
                        shortUserId(s.principal_user_id);

                      return (
                        <div
                          key={s.id}
                          className="flex items-center justify-between gap-2 text-xs"
                        >
                          <div className="min-w-0 truncate">
                            {recipientLabel}
                            <span className="text-muted-foreground">
                              {" "}
                              • {s.permission}
                              {s.revoked_at ? " • revoked" : ""}
                            </span>
                          </div>

                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleRevokeShare(s.id)}
                            disabled={
                              shareLoading || !!s.revoked_at
                            }
                          >
                            Revoke
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </ModalPanel>
      </DialogContent>
      </Dialog>

      {/*Filter Modal*/}
      <Dialog open={filtersOpen} onOpenChange={setFiltersOpen}>
        <DialogContent className="max-w-[560px] p-0 border-0 bg-transparent [&>button]:hidden">
          <ModalPanel className="p-4 max-h-[80vh] overflow-y-auto">

            {/* Project Filter */}
            <div className="px-1 pb-2 space-y-1">
              <div className="text-[11px] font-semibold">Project</div>

              <div className="space-y-1">
                <select
                  value={projectClientFilter}
                  onChange={(e) => {
                    setProjectClientFilter(e.target.value);
                    setProjectLocationFilter("all");
                    setProjectFilter("all");
                  }}
                  className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                >
                  <option value="all">All clients</option>
                  {filterClientOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>

                <select
                  value={projectLocationFilter}
                  onChange={(e) => {
                    setProjectLocationFilter(e.target.value);
                    setProjectFilter("all");
                  }}
                  disabled={filterLocationOptions.length === 0}
                  className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                >
                  <option value="all">All locations</option>
                  {filterLocationOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>

                <select
                  value={projectFilter}
                  onChange={(e) => {
                    const value = e.target.value;
                    setProjectFilter(value);
                    if (value === "__misc__") {
                      setProjectClientFilter("all");
                      setProjectLocationFilter("all");
                    }
                  }}
                  className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                >
                  <option value="all">All projects</option>
                  <option value="__misc__">Misc</option>
                  {filterProjectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status */}
            <div className="space-y-1">
              <div className="text-[11px] font-semibold">Status</div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value as TaskStatus | "all")}
                className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
              >
                <option value="all">All statuses</option>
                {STATUS_OPTIONS.map((opt) => (
                  <option key={opt} value={opt}>{getStatusLabel(opt)}</option>
                ))}
              </select>
            </div>

            {/* Type + Discipline */}
            <div className="grid grid-cols-2 gap-2 mt-3">
              <div className="space-y-1">
                <div className="text-[11px] font-semibold">Task type</div>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value as TaskType | "all")}
                  className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                >
                  <option value="all">All types</option>
                  {TYPE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-1">
                <div className="text-[11px] font-semibold">Discipline</div>
                <select
                  value={disciplineFilter}
                  onChange={(e) => setDisciplineFilter(e.target.value as TaskDiscipline | "all")}
                  className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
                >
                  <option value="all">All disciplines</option>
                  {DISCIPLINE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Priority */}
            <div className="space-y-1 mt-3">
              <div className="text-[11px] font-semibold">Priority</div>
              <select
                value={priorityFilter}
                onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | "all")}
                className="w-full text-[11px] border rounded px-2 py-1 bg-[#1a1a1a] text-white"
              >
                <option value="all">All priorities</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            {/* Clear */}
            <div className="pt-3 flex justify-end">
              <button
                type="button"
                className="text-[11px] px-2 py-1 rounded border border-border bg-window hover:bg-window/80"
                onClick={clearAllFilters}
              >
                Clear filters
              </button>
            </div>

          </ModalPanel>
        </DialogContent>
      </Dialog>

      
      <TaskDetailDrawer
        task={selectedTask}
        projectById={projectById}
        onClose={() => setSelectedTask(null)}
      />

      {/* Task Modal */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent
          className="
            w-full
            max-w-[500px]
            bg-transparent
            border-none
            p-0
            [&>button]:hidden
          "
        >
        <ModalPanel>
          <DialogHeader>
            <DialogTitle>Task Overview</DialogTitle>
            <DialogDescription>
              Input task details
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">

            <div className="flex flex-col gap-1">
              <FieldLabel>Title</FieldLabel>
              <input
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
                className={formControlClass}
              />
            </div>

            <div className="flex flex-col gap-1">
              <FieldLabel>Description</FieldLabel>
              <textarea
                value={formDescription}
                onChange={(e) => setFormDescription(e.target.value)}
                className={`${formControlClass} min-h-[120px]`}
              />
            </div>

          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={formIsMiscScoped}
              onChange={(e) => {
                const checked = e.target.checked;
                setFormIsMiscScoped(checked);
                if (checked) {
                  setFormClientKey("");
                  setFormLocationKey("");
                  setFormProjectId("");
                }
              }}
              className="accent-[#7b2ee6] h-4 w-4"
            />
            <span className="text-sm text-muted-foreground">Quick task (Misc)</span>
          </label>

          <div className="grid grid-cols-2 gap-x-10 gap-y-4">

            {/* LEFT COLUMN */}
            {!formIsMiscScoped && (
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <FieldLabel>Client</FieldLabel>
                <select
                  value={formClientKey}
                  onChange={(e) => {
                    setFormClientKey(e.target.value);
                    setFormLocationKey("");
                    setFormProjectId("");
                  }}
                  className={formControlClass}
                >
                  <option value="">—</option>
                  {formClientOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Location</FieldLabel>
                <select
                  value={formLocationKey}
                  onChange={(e) => {
                    setFormLocationKey(e.target.value);
                    setFormProjectId("");
                  }}
                  className={formControlClass}
                >
                  <option value="">—</option>
                  {formLocationOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Project</FieldLabel>
                <select
                  value={formProjectId}
                  onChange={(e) => setFormProjectId(e.target.value)}
                  className={formControlClass}
                >
                  <option value="">Misc</option>
                  {formProjectOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
            </div>
            )}

            {/* RIGHT COLUMN */}
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1">
                <FieldLabel>Discipline</FieldLabel>
                <select
                  value={formDiscipline}
                  onChange={(e) => setFormDiscipline(e.target.value as TaskDiscipline)}
                  className={formControlClass}
                >
                  <option value="">—</option>
                  {DISCIPLINE_OPTIONS.map((d) => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Type</FieldLabel>
                <select
                  value={formType}
                  onChange={(e) => setFormType(e.target.value as TaskType)}
                  className={formControlClass}
                >
                  {TYPE_OPTIONS.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-1">
                <FieldLabel>Task Due</FieldLabel>
                <input
                  type="date"
                  value={formDueDate}
                  onChange={(e) => setFormDueDate(e.target.value)}
                  className={formControlClass}
                />
              </div>
            </div>
          </div>

          </div>

          <DialogFooter className="flex justify-end gap-3 pt-4">

            {/* Cancel */}
            <Button
              variant="secondary"
              className="
                rounded-2xl
                px-4
                bg-black/40
                ring-1 ring-white/10
                hover:bg-white/10
                transition
              "
              onClick={() => setEditing(null)}
            >
              Cancel
            </Button>

            {/* Create / Save */}
            <Button
              className="
                rounded-2xl
                px-5
                bg-[#7b2ee6]/90
                ring-1 ring-[#7b2ee6]/60
                shadow-[0_0_25px_rgba(123,46,230,0.35)]
                hover:bg-[#7b2ee6]
                hover:shadow-[0_0_40px_rgba(123,46,230,0.55)]
                transition-all
              "
              onClick={async () => {
                if (!editing?.id) {
                  await handleCreateInlineFromForm();
                } else {
                  await handleSaveEdit();
                }
              }}
            >
              {editing?.id ? "Save" : "Create"}
            </Button>

          </DialogFooter>
          </ModalPanel>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDeleteTask} onOpenChange={(open) => { if (!open) setConfirmDeleteTask(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this task? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { if (confirmDeleteTask) executeDeleteTask(confirmDeleteTask); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
